import { useQuery } from "@tanstack/react-query";
import { httpClient } from "@coworker/http-client";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  DEFAULT_TASK_PAGE_SIZE,
  TaskTypeNames,
} from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { getRelatedTasksByProductQueryKey } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import { getTasksByItem } from "@coworker/apprestructured/src/task-management/services/tasks.service";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { TasksWithArticle } from "@coworker/types/lib/tasks";

async function fetchRelatedTasksForProduct(shortId: string) {
  const result = await httpClient.tasks.get<{
    data: { list: TasksWithArticle[] };
  }>("/v1/tasks/for_product", { params: { short_id: shortId } });
  return result.data.list;
}

async function fetchAllV2TasksForProduct(
  shortId: string,
  storeId: string,
  taskTypeIds: string[]
) {
  const tasks = [];
  let cursor: string | undefined;

  do {
    const page = await getTasksByItem(
      shortId,
      storeId,
      taskTypeIds,
      DEFAULT_TASK_PAGE_SIZE,
      cursor
    );
    tasks.push(...page.tasks);
    cursor = page.lastSeenCreatedAt ?? undefined;

    if (tasks.length >= page.totalCount) break;
  } while (cursor);

  return tasks;
}

export function useRelatedTasksForProduct(shortId: string) {
  const storeId = useStoreId();
  const { data: taskTypes } = useAllTaskTypes();
  const v2TaskTypeIds =
    taskTypes
      ?.filter(({ name }) => name !== TaskTypeNames.PRODUCT_QUALITY)
      .map(({ id }) => id) ?? [];

  return useQuery({
    enabled: Boolean(shortId && storeId && taskTypes),
    queryKey: getRelatedTasksByProductQueryKey(shortId, storeId, v2TaskTypeIds),
    queryFn: async () => {
      const [v1Tasks, v2Tasks] = await Promise.all([
        fetchRelatedTasksForProduct(shortId),
        fetchAllV2TasksForProduct(shortId, storeId, v2TaskTypeIds),
      ]);
      return {
        list: [...v1Tasks, ...v2Tasks],
        v1Tasks,
        v2Tasks,
      };
    },
    staleTime: 0,
  });
}
